import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import { Button as ButtonPrimitive } from '../ui/Button';
import { MultilinkLink } from '../ui/MultilinkLink';
import type { ButtonStoryblok, StoryblokMultilink } from '../../types/storyblok';

interface ButtonBlokProps {
  blok: ButtonStoryblok;
}

type ResolvedVariant = 'primary' | 'secondary' | 'inverse';
type ResolvedSize = 'lg' | 'md' | 'sm' | 'xs';
type AuthoredWeight = 'bold' | 'semibold' | 'medium';
type PrimaryWeight = 'bold' | 'semibold';
type SecondaryWeight = 'bold' | 'medium';

// Schema fields default the editor's empty-string sentinel to the spec'd default
// (R2 acceptance criteria; design.md → "Schema-level coercion to watch for").
const resolveVariant = (variant: ButtonStoryblok['variant']): ResolvedVariant => {
  if (variant === 'secondary' || variant === 'inverse') return variant;
  return 'primary';
};

const resolveSize = (size: ButtonStoryblok['size']): ResolvedSize => {
  if (size === 'lg' || size === 'sm' || size === 'xs') {
    return size;
  }
  return 'md';
};

const resolveAuthoredWeight = (weight: ButtonStoryblok['weight']): AuthoredWeight => {
  if (weight === 'semibold' || weight === 'medium') {
    return weight;
  }
  return 'bold';
};

// Typed overload (design.md → "Weight Resolver"):
//   primary + medium → bold (R2.2)
//   secondary + semibold → bold (R2.3)
//   every other combination passes through unchanged.
function resolveWeight(variant: 'primary', weight: AuthoredWeight): PrimaryWeight;
function resolveWeight(variant: 'secondary', weight: AuthoredWeight): SecondaryWeight;
function resolveWeight(
  variant: 'primary' | 'secondary',
  weight: AuthoredWeight
): PrimaryWeight | SecondaryWeight {
  if (variant === 'primary') {
    return weight === 'medium' ? 'bold' : weight;
  }
  return weight === 'semibold' ? 'bold' : weight;
}

// Discriminated-union narrowing for the multilink: only the `email` linktype
// carries an `email` field, so guard before reading it.
const linkEmail = (link: StoryblokMultilink): string | undefined =>
  link.linktype === 'email' ? link.email : undefined;

// R3.6: a link is unusable when every routable field is empty/undefined. The
// caller renders an inert `<a href="#">` placeholder so the broken state is
// editor-visible without crashing.
const isLinkEmpty = (link: StoryblokMultilink | undefined): boolean => {
  if (!link) return true;
  const cached = link.cached_url ?? '';
  const url = link.url ?? '';
  const email = linkEmail(link) ?? '';
  return cached === '' && url === '' && email === '';
};

// Inline trailing right-arrow icon (Figma node 296:3206). `currentColor` so the
// stroke matches the surrounding text color in any variant. Marked aria-hidden
// since the button label already carries the accessible name.
const ArrowIcon: React.FC = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="ml-2 inline-block h-4 w-4 shrink-0"
  >
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

// Render the design-system primary/secondary primitive with the resolved props.
// Branching on variant lets the discriminated `PrimaryButtonProps |
// SecondaryButtonProps` union narrow so each branch passes a weight TypeScript
// accepts for that variant. `withArrow` appends the inline ArrowIcon as a child
// of the primitive (the primitive's `children: ReactNode` prop accepts it).
const renderPrimitive = (
  variant: 'primary' | 'secondary',
  size: ResolvedSize,
  weight: AuthoredWeight,
  label: string,
  withArrow: boolean
): JSX.Element => {
  if (variant === 'primary') {
    return (
      <ButtonPrimitive variant="primary" size={size} weight={resolveWeight('primary', weight)}>
        {label}
        {withArrow && <ArrowIcon />}
      </ButtonPrimitive>
    );
  }
  return (
    <ButtonPrimitive variant="secondary" size={size} weight={resolveWeight('secondary', weight)}>
      {label}
      {withArrow && <ArrowIcon />}
    </ButtonPrimitive>
  );
};

// Size → spacing/typography mapping for the inverse variant (mirrors the
// design-system primitive's height/text-style tokens for consistency across
// variants).
const inverseSizeClass: Record<ResolvedSize, string> = {
  lg: 'h-16 px-8 text-button-large',
  md: 'h-12 px-8 text-button-mid',
  sm: 'h-10 px-6 text-button-mid',
  xs: 'h-8 px-4 text-button-small',
};

// Render the inverse variant: CTA on a deep-blue surface (Figma node 296:3206).
// Mirrors Hero.tsx's primary-CTA pattern on main — pale ice-blue surface with
// deep-blue text and a subtle elevation shadow. The button itself is a `<span>`
// since the surrounding MultilinkLink (or empty-link fallback) provides the
// `<a>` semantics; we therefore avoid nesting an interactive `<button>` inside
// an `<a>`. The trailing arrow is rendered when `with_arrow` is true.
const renderInverse = (size: ResolvedSize, label: string, withArrow: boolean): JSX.Element => (
  <span
    className={[
      'inline-flex items-center justify-center rounded-sm font-primary font-semibold',
      'bg-secondary-ice-blue text-brand-deep',
      'drop-shadow-[0_4px_8px_rgba(91,184,245,0.32)]',
      'transition-colors hover:bg-secondary-light-sky',
      inverseSizeClass[size],
    ].join(' ')}
  >
    {label}
    {withArrow && <ArrowIcon />}
  </span>
);

const renderChild = (
  variant: ResolvedVariant,
  size: ResolvedSize,
  weight: AuthoredWeight,
  label: string,
  withArrow: boolean
): JSX.Element => {
  if (variant === 'inverse') {
    return renderInverse(size, label, withArrow);
  }
  return renderPrimitive(variant, size, weight, label, withArrow);
};

export const Button = ({ blok }: ButtonBlokProps): JSX.Element => {
  const editableAttrs = storyblokEditable(blok as unknown as SbBlokData);
  const variant = resolveVariant(blok.variant);
  const size = resolveSize(blok.size);
  const weight = resolveAuthoredWeight(blok.weight);
  const withArrow = blok.with_arrow ?? false;
  const child = renderChild(variant, size, weight, blok.label, withArrow);

  // R3.6: empty-link fallback. Render the styled label inside an inert anchor
  // carrying the same editable attributes, so editors still see the broken
  // state in preview without a runtime crash.
  if (isLinkEmpty(blok.link)) {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid -- R3.6: deliberate inert href so the broken state is editor-visible in preview
      <a href="#" {...editableAttrs}>
        {child}
      </a>
    );
  }

  // Normal path (R3.1–R3.5, R3.7, R4.1, R5.2, R5.3): MultilinkLink resolves
  // the href/target/rel per linktype and forwards `editableAttrs` onto the
  // rendered anchor root, so no extra wrapping element is introduced.
  return (
    <MultilinkLink to={blok.link} editableAttrs={editableAttrs}>
      {child}
    </MultilinkLink>
  );
};
